import { useFormikContext } from "formik";
import { FormField } from "@/components/common/form-field";
import { DateField, PhoneField, SsnField } from "@/components/common/masked-form-field";
import {
  CheckboxField,
  SelectField,
} from "@/features/portal/components/onboarding/onboarding-fields";
import {
  PASSPORT_OPTIONS,
  PREF_CONTACT_OPTIONS,
  STATE_OPTIONS,
} from "@/features/portal/components/onboarding/onboarding-options";
import {
  type TabProps,
  useBlurSave,
} from "@/features/portal/components/onboarding/tabs/tab-shared";
import type { OnboardingValues } from "@/features/portal/components/onboarding/wizard-schema";

/** Personal Information tab (individual clients). */
export function PersonalTab({ save }: TabProps) {
  const { values } = useFormikContext<OnboardingValues>();
  const onBlur = useBlurSave(save);

  return (
    <div className="grid gap-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <FormField name="first_name" label="First Name" onBlur={onBlur("first_name")} />
        <FormField
          name="middle_initial"
          label="Middle Initial"
          maxLength={1}
          onBlur={onBlur("middle_initial")}
        />
        <FormField name="last_name" label="Last Name" onBlur={onBlur("last_name")} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <SsnField name="ssn" label="Social Security Number" onBlur={onBlur("ssn")} />
        <DateField
          name="date_of_birth"
          label="Date of Birth"
          onBlur={onBlur("date_of_birth")}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          name="driver_license"
          label="Driver's License #"
          maxLength={20}
          onBlur={onBlur("driver_license")}
        />
        <SelectField
          name="license_state"
          label="License State"
          options={STATE_OPTIONS}
          placeholder="Select state"
          onCommit={save}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <PhoneField name="home_phone" label="Home Phone" onBlur={onBlur("home_phone")} />
        <PhoneField name="mobile_phone" label="Mobile Phone" onBlur={onBlur("mobile_phone")} />
        <PhoneField name="work_phone" label="Work Phone" onBlur={onBlur("work_phone")} />
        <PhoneField name="work_mobile" label="Work Mobile" onBlur={onBlur("work_mobile")} />
        <PhoneField name="fax_number" label="Fax" onBlur={onBlur("fax_number")} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          name="preferred_contact_method"
          label="Preferred Contact Method"
          options={PREF_CONTACT_OPTIONS}
          onCommit={save}
        />
        <SelectField
          name="passport_on_file"
          label="Passport on File"
          options={PASSPORT_OPTIONS}
          onCommit={save}
        />
      </div>

      <FormField name="salutation" label="Salutation" onBlur={onBlur("salutation")} />

      <div className="grid gap-3 border-t pt-4">
        <CheckboxField name="is_trustee" label="This person is a trustee" onCommit={save} />
        {values.is_trustee && (
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField name="trustee_name" label="Trustee Name" onBlur={onBlur("trustee_name")} />
            <FormField name="trustee_role" label="Trustee Role" onBlur={onBlur("trustee_role")} />
          </div>
        )}
      </div>

      <div className="grid gap-3 border-t pt-4">
        <CheckboxField name="is_deceased" label="This person is deceased" onCommit={save} />
        {values.is_deceased && (
          <div className="sm:max-w-xs">
            <DateField
              name="date_of_death"
              label="Date of Death"
              onBlur={onBlur("date_of_death")}
            />
          </div>
        )}
      </div>
    </div>
  );
}
